import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import axiosInstance from 'src/api/axiosInstance';

// ─────────────────────────────────────────────
// TrialBanner
// Shows a subtle upgrade prompt when the tenant
// is on the Free plan.
// ─────────────────────────────────────────────
export default function TrialBanner() {
  const navigate = useNavigate();
  const location = useLocation();

  const isDashboardPage =
    location.pathname === '/dashboard' ||
    location.pathname === '/dashboard/' ||
    location.pathname === '/dashboard/app' ||
    location.pathname === '/dashboard/app/';

  const token = sessionStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user'));

  const subscr = JSON.parse(localStorage.getItem('selectedSubscrSite'));
  const [currentPlan, setCurrentPlan] = useState(null);

  const isSuperAdmin = subscr?.roleName?.trim().toLowerCase() === 'super admin';

  const fetchCurrentPlan = async () => {
    try {
      const response = await axiosInstance.get(
        '/rest/dynamic-billing/current-plan',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setCurrentPlan(response.data);

      return response.data;
    } catch (error) {
      console.error('CURRENT_PLAN_ERROR', error);
      return null;
    }
  };

  useEffect(() => {
    fetchCurrentPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isDashboardPage || !isSuperAdmin || !currentPlan) {
    return null;
  }

  const isPlanFree = currentPlan?.planCode?.toUpperCase() === 'FREE';
  const isExpired = currentPlan?.expired === true;
  const remainingDays = currentPlan?.remainingDays || 0;
  const isExpiringSoon = currentPlan?.expiringSoon === true || (remainingDays > 0 && remainingDays <= 7);
  const isTrial =
    currentPlan?.subscriptionStatus?.toUpperCase() === 'TRIAL';

  // Only show banner for Free plan, Expired, Expiring Soon, or Trial
  if (!isPlanFree && !isTrial && !isExpired && !isExpiringSoon) {
    return null;
  }

  const cleanPlanName = (currentPlan?.planName || '').replace(/\s+v\d+/i, '').trim();

  let messageText =
    'You are on the Free plan. Some data may be restricted. Upgrade for full access.';
  let buttonText = 'Upgrade Now';
  let bannerColor = 'warning';
  let iconName = 'solar:lock-bold-duotone';

  if (isTrial) {
    messageText = `Your free trial of the ${cleanPlanName} Plan is expiring in ${currentPlan.remainingDays} day${currentPlan.remainingDays > 1 ? 's' : ''}. Upgrade now to ensure uninterrupted service.`;
    buttonText = 'Upgrade Now';
    bannerColor = 'warning';
    iconName = 'solar:clock-circle-bold-duotone';
    if (isExpired) {
      messageText = `Your free trial of the ${cleanPlanName} Plan has expired. Please upgrade now to maintain access to your workspace.`;
      bannerColor = 'error';
      iconName = 'solar:danger-bold-duotone';
    }
  } else if (isExpired) {
    messageText = `Your subscription for the ${cleanPlanName} Plan has expired. Renew now to restore team access.`;
    buttonText = 'Renew Plan';
    bannerColor = 'error';
    iconName = 'solar:danger-bold-duotone';
  } else if (isExpiringSoon) {
    messageText = `Your subscription for the ${cleanPlanName} Plan expires in ${currentPlan.remainingDays} day${currentPlan.remainingDays > 1 ? 's' : ''}. Renew early to lock in your current rate.`;
    buttonText = 'Renew Early';
    bannerColor = 'warning';
    iconName = 'solar:bell-bing-bold-duotone';
  }

  return (
    <Box
      sx={{
        mt: 2,
        px: 2.5,
        py: 1,
        background: (theme) =>
          `linear-gradient(135deg, ${alpha(theme.palette[bannerColor].main, 0.12)} 0%, ${alpha(
            theme.palette[bannerColor].main,
            0.08
          )} 100%)`,
        borderBottom: (theme) =>
          `1px solid ${alpha(theme.palette[bannerColor].main, 0.3)}`,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Iconify
            icon={iconName}
            width={20}
            sx={{ color: `${bannerColor}.main` }}
          />

          <Typography variant="body2" fontWeight={600} color={`${bannerColor}.dark`}>
            {messageText}
          </Typography>
        </Stack>

        <Button
          size="small"
          variant="contained"
          color={bannerColor}
          onClick={() => window.open('/pricing', '_blank', 'noopener,noreferrer')}
          sx={{
            borderRadius: '8px',
            fontWeight: 700,
            fontSize: '0.75rem',
            px: 2,
            py: 0.5,
            textTransform: 'none',
            color: '#fff',
          }}
        >
          {buttonText}
        </Button>
      </Stack>
    </Box>
  );
}